import { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import Navbar from '../../Navbar';
import Footer from '../../Footer';
import { toast } from 'react-toastify';
import MenuCostForm from './MenuCostForm';
import { useLocation, useNavigate } from 'react-router-dom';
import OutletSearch from './../../SelectSearch/OutletSearch';
import { convertToBaseQuantity, convertToDisplayUnit } from '../../../utils/quantityConversion';
import { gramVariations, kgVariations, literVariations, milliliterVariations } from '../../../data/unitVariations';

const MenuCostPrice = () => {
    const baseUrl = process.env.REACT_APP_BASE_URL;
    const token = localStorage.getItem("token");
    const navigate = useNavigate();

    const [menuItems, setMenuItems] = useState([]);
    const [unitDefinitions, setUnitDefinitions] = useState([]);
    const [subRecipes, setSubRecipes] = useState([]);
    const [selectedOutlet, setSelectedOutlet] = useState("");
    const [costItems, setCostItems] = useState([]);
    const [subRecipeItems, setSubRecipeItems] = useState([]);
    const [editingIndex, setEditingIndex] = useState(null);
    const [editingSubRecipeIndex, setEditingSubRecipeIndex] = useState(null);
    const [showSubRecipe, setShowSubRecipe] = useState(false);
    const [type, setType] = useState("Food");
    const [recipeType, setRecipeType] = useState("Menu");
    const [groupName, setGroupName] = useState("");
    const [autoFill, setAutoFill] = useState(false);
    const [manualTotalCost, setManualTotalCost] = useState(0);
    const [draftCostItem, setDraftCostItem] = useState(null);
    const [draftSubRecipeItem, setDraftSubRecipeItem] = useState(null);

    const location = useLocation();
    const params = new URLSearchParams(location.search);
    const outletFromUrl = params.get("outlet");
    const incomingRecipe = location.state?.recipeData;

    useEffect(() => {
        if (outletFromUrl) {
            setSelectedOutlet(outletFromUrl);
        }
    }, [outletFromUrl]);

    const [formData, setFormData] = useState({
        menuItem: '',
        sellingPrice: '',
        costItem: '',
        rate: '',
        unit: '',
        uom: '',
        quantity: '',
        new_uom: '',
        link_id: '',
        recipe_id: ''
    });

    const [subRecipeFormData, setSubRecipeFormData] = useState({
        subRecipe: '',
        subRecipeName: '',
        quantity: '',
        uom: '',
        rate: '',
        unit: '',
        new_uom: '',
        link_id: '',
        sub_recipe_id: ''
    });

    useEffect(() => {
        if (!incomingRecipe) return;

        console.log(JSON.stringify(incomingRecipe))

        setFormData(prev => ({
            ...prev,
            menuItem: incomingRecipe.name,
            sellingPrice: incomingRecipe.sellingprice
        }));

        const mappedCostItems = incomingRecipe.items.map(item => ({
            costItem: item.name,
            rate: item.rate,
            unit: item.unit,
            uom: item.uom,
            new_uom: item.new_uom || item.uom,
            quantity: convertToDisplayUnit(
                item.quantity,
                item.uom,
                item.new_uom,
                item.unit
            ),
            costPrice: item.cost,
            link_id: item.id,
            recipe_id: item.recipe_id,
        }));

        const mappedSubRecipes = incomingRecipe.sub_recipes?.map(sub => ({
            subRecipeId: sub.sub_recipe_id,
            subRecipeName: sub.name,
            quantity:
                convertToDisplayUnit(
                    sub.quantity,
                    sub.uom,
                    sub.new_uom,
                    sub.unit
                ),
            uom: sub.uom,
            new_uom: sub.new_uom || sub.uom,
            rate: sub.rate,
            unit: sub.unit,
            costprice: sub.costprice,
            link_id: sub.link_id,
            sub_recipe_id: sub.sub_recipe_id,
        })) || [];

        setType(incomingRecipe.ItemType);
        setGroupName(incomingRecipe.GroupName);

        setRecipeType(
            incomingRecipe.GroupName?.trim().toLowerCase() === "central kitchen" ? "Production" : "Menu"
        );

        setCostItems(mappedCostItems);
        setSubRecipeItems(mappedSubRecipes);

    }, [incomingRecipe]);

    const calculateCost = (quantity, unit, rate, uom, new_uom) => {
        const convertedQty = convertToBaseQuantity({
            quantity,
            fromUom: uom,
            toUom: new_uom,
            kgVars: kgVariations,
            gVars: gramVariations,
            lVars: literVariations,
            mlVars: milliliterVariations
        });

        return (convertedQty / parseFloat(unit)) * parseFloat(rate);
    };

    useEffect(() => {
        const fetchMenuItems = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.post(`${baseUrl}/item-lists`, {
                    token: token,
                    outlet: selectedOutlet
                });
                setMenuItems(response.data);
            } catch (error) {
                console.error('Error fetching menu items:', error);
            }
        };

        const fetchUnitDefinitions = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.get(`${baseUrl}/unit-definitions`, {
                    params: {
                        outlet: selectedOutlet,
                        token: token
                    }
                });
                setUnitDefinitions(response.data);
            } catch (error) {
                console.error('Error fetching unit definitions:', error);
            }
        };

        const fetchSubRecipes = async () => {
            if (!token || !selectedOutlet) return;

            try {
                const response = await axios.get(`${baseUrl}/sub-recipes`, {
                    params: {
                        token: token,
                        outlet: selectedOutlet
                    }
                });
                setSubRecipes(response.data);
            } catch (error) {
                console.error('Error fetching sub-recipes:', error);
            }
        };

        fetchMenuItems();
        fetchUnitDefinitions();
        fetchSubRecipes();
    }, [token, selectedOutlet, baseUrl]);

    const handleInputChange = (e) => {
        const { name, value } = e.target;

        if ((name === "unit" || name === "quantity") && value && isNaN(value)) return;

        setFormData(prev => {
            const updated = { ...prev, [name]: value };

            if (draftCostItem && (name === 'quantity' || name === 'new_uom')) {
                const qty = name === 'quantity' ? value : prev.quantity;
                const new_uom = name === 'new_uom' ? value : prev.new_uom;
                const costPrice = qty && prev.rate && prev.unit
                    ? calculateCost(qty, prev.unit, prev.rate, prev.uom, new_uom || prev.uom)
                    : 0;

                setDraftCostItem(d => ({
                    ...d,
                    quantity: qty,
                    new_uom: new_uom || prev.uom,
                    costPrice: isNaN(costPrice) ? 0 : costPrice,
                }));
            }

            return updated;
        });
    };

    const handleRecipeTypeChange = (e) => {
        const value = e.target.value;

        setRecipeType(value);

        if (value === "Production") {
            setGroupName("Central Kitchen");
        } else {
            setGroupName("");
        }
    };

    const handleSubRecipeInputChange = (e) => {
        const { name, value } = e.target;

        setSubRecipeFormData(prev => {
            const updated = { ...prev, [name]: value };

            if (draftSubRecipeItem && (name === 'quantity' || name === 'new_uom')) {
                const qty = name === 'quantity' ? value : prev.quantity;
                const new_uom = name === 'new_uom' ? value : prev.new_uom;
                const costPrice = qty && prev.rate && prev.unit
                    ? calculateCost(qty, prev.unit, prev.rate, prev.uom, new_uom || prev.uom)
                    : 0;

                setDraftSubRecipeItem(d => ({
                    ...d,
                    quantity: qty,
                    new_uom: new_uom || prev.uom,
                    costprice: isNaN(costPrice) ? 0 : costPrice,
                }));
            }

            return updated;
        });
    };

    const handleMenuItemSelect = (e) => {
        const itemName = e.target.value;

        const selectedItem = menuItems
            .flatMap(type => type.items.flatMap(category => category.items))
            .find(item => item.Name === itemName);

        if (selectedItem) {
            setFormData({
                ...formData,
                menuItem: selectedItem.Name,
                sellingPrice: selectedItem.Price,
                costItem: '',
                rate: '',
                unit: '',
                uom: '',
                quantity: '',
                new_uom: '',
                link_id: '',
                recipe_id: '',
            });

            setType(selectedItem.Type);
            setRecipeType('Menu');
            setGroupName(selectedItem.Description);
        } else {
            setFormData({
                ...formData,
                menuItem: itemName,
                sellingPrice: '',
                costItem: '',
                rate: '',
                unit: '',
                uom: '',
                quantity: '',
                new_uom: '',
                link_id: '',
                recipe_id: '',
            });

            setType('Food');
            setRecipeType('Menu');
            setGroupName("");
        }
    };

    const handleClearMenuItem = () => {
        setFormData({ menuItem: '', sellingPrice: '', costItem: '', rate: '', unit: '', uom: '', quantity: '', new_uom: '', link_id: '', sub_recipe_id: '' });
        setCostItems([]);
        setSubRecipeItems([]);
        setDraftCostItem(null);
        setDraftSubRecipeItem(null);
        setEditingIndex(null);
        setEditingSubRecipeIndex(null);
        setAutoFill(false);
    }

    const handleCostItemSelect = (e) => {
        const itemName = e.target.value;
        const selectedItem = unitDefinitions.find(item => item.name === itemName);

        if (selectedItem) {
            const showConversionOptions =
                kgVariations.includes(selectedItem.uom.toLowerCase()) ||
                literVariations.includes(selectedItem.uom.toLowerCase()) ||
                gramVariations.includes(selectedItem.uom.toLowerCase()) ||
                milliliterVariations.includes(selectedItem.uom.toLowerCase());

            let normalizedUom = selectedItem.uom.toLowerCase();
            if (kgVariations.includes(normalizedUom)) normalizedUom = 'kg';
            else if (gramVariations.includes(normalizedUom)) normalizedUom = 'g';
            else if (literVariations.includes(normalizedUom)) normalizedUom = 'ltr';
            else if (milliliterVariations.includes(normalizedUom)) normalizedUom = 'ml';

            const defaultNewUom = showConversionOptions
                ? (kgVariations.includes(normalizedUom) || normalizedUom === 'g' ? 'gm' : 'ml')
                : normalizedUom;

            setFormData({
                ...formData,
                costItem: selectedItem.name,
                rate: selectedItem.rate,
                unit: selectedItem.unit,
                uom: normalizedUom,
                new_uom: showConversionOptions ? defaultNewUom : normalizedUom,
                quantity: '',
            });

            // Immediately add a draft row to the table
            setDraftCostItem({
                menuItem: formData.menuItem,
                sellingPrice: formData.sellingPrice,
                costItem: selectedItem.name,
                rate: selectedItem.rate,
                unit: selectedItem.unit,
                uom: normalizedUom,
                new_uom: showConversionOptions ? defaultNewUom : normalizedUom,
                quantity: '',
                costPrice: 0,
                link_id: '',
                recipe_id: '',
                isDraft: true,
            });
        }
    };

    const handleSubRecipeSelect = (e) => {
        const subRecipeId = parseInt(e.target.value);
        const selectedSubRecipe = subRecipes.find(recipe => recipe.id === subRecipeId);

        if (selectedSubRecipe) {
            const showConversionOptions =
                kgVariations.includes(selectedSubRecipe.uom.toLowerCase()) ||
                gramVariations.includes(selectedSubRecipe.uom.toLowerCase()) ||
                literVariations.includes(selectedSubRecipe.uom.toLowerCase()) ||
                milliliterVariations.includes(selectedSubRecipe.uom.toLowerCase());

            let normalizedUom = selectedSubRecipe.uom.toLowerCase();
            if (kgVariations.includes(normalizedUom)) normalizedUom = 'kg';
            else if (gramVariations.includes(normalizedUom)) normalizedUom = 'g';
            else if (literVariations.includes(normalizedUom)) normalizedUom = 'ltr';
            else if (milliliterVariations.includes(normalizedUom)) normalizedUom = 'ml';

            const defaultNewUom = showConversionOptions
                ? (kgVariations.includes(normalizedUom) || normalizedUom === 'g' ? 'gm' : 'ml')
                : normalizedUom;

            setSubRecipeFormData({
                ...subRecipeFormData,
                subRecipe: selectedSubRecipe.id,
                subRecipeName: selectedSubRecipe.name,
                rate: selectedSubRecipe.costprice || '',
                uom: selectedSubRecipe.uom || '',
                unit: selectedSubRecipe.unit || '',
                new_uom: showConversionOptions ? defaultNewUom : normalizedUom,
                sub_recipe_id: selectedSubRecipe.id,
                quantity: '',
            });

            // Immediately add a draft row to the table
            setDraftSubRecipeItem({
                subRecipeId: selectedSubRecipe.id,
                subRecipeName: selectedSubRecipe.name,
                rate: selectedSubRecipe.costprice || '',
                uom: selectedSubRecipe.uom || '',
                unit: selectedSubRecipe.unit || '',
                new_uom: showConversionOptions ? defaultNewUom : normalizedUom,
                sub_recipe_id: selectedSubRecipe.id,
                quantity: '',
                costprice: 0,
                link_id: '',
                isDraft: true,
            });
        }
    };

    const handleUomChange = (e) => {
        const new_uom = e.target.value;
        setFormData(prev => {
            const costPrice = prev.quantity && prev.rate && prev.unit
                ? calculateCost(prev.quantity, prev.unit, prev.rate, prev.uom, new_uom)
                : 0;

            if (draftCostItem) {
                setDraftCostItem(d => ({
                    ...d,
                    new_uom,
                    costPrice: isNaN(costPrice) ? 0 : costPrice,
                }));
            }

            return { ...prev, new_uom };
        });
    };

    const handleAddCostItem = (e) => {
        e.preventDefault();

        if (!formData.menuItem || !formData.costItem || !formData.quantity ||
            (['kg', 'ltr'].includes(formData.uom) && !formData.new_uom)) {
            toast.info('Please fill all required fields');
            return;
        }

        const exists = costItems.some(
            (item, idx) => item.costItem === formData.costItem && idx !== editingIndex
        );
        if (exists) {
            toast.info('This ingredient already exists');
            return;
        }

        const costPrice = calculateCost(
            formData.quantity, formData.unit, formData.rate, formData.uom, formData.new_uom
        );

        const confirmedItem = {
            menuItem: formData.menuItem,
            sellingPrice: formData.sellingPrice,
            costItem: formData.costItem,
            rate: formData.rate,
            unit: formData.unit,
            uom: formData.uom,
            new_uom: formData.new_uom || formData.uom,
            quantity: formData.quantity,
            costPrice,
            link_id: formData.link_id || '',
            recipe_id: formData.recipe_id || '',
            isDraft: false,
        };

        if (editingIndex !== null) {
            const updated = [...costItems];
            updated[editingIndex] = confirmedItem;
            setCostItems(updated);
            setEditingIndex(null);
        } else {
            // Replace draft row if exists, else push
            setCostItems(prev => {
                const withoutDraft = prev.filter(i => !i.isDraft);
                return [...withoutDraft, confirmedItem];
            });
        }

        setDraftCostItem(null);
        setFormData(prev => ({
            ...prev, costItem: '', rate: '', unit: '', uom: '', quantity: '', new_uom: ''
        }));
    };

    const handleAddSubRecipe = (e) => {
        e.preventDefault();

        if (!subRecipeFormData.subRecipe || !subRecipeFormData.quantity ||
            !subRecipeFormData.subRecipeName ||
            (['kg', 'ltr'].includes(subRecipeFormData.uom) && !subRecipeFormData.new_uom)) {
            toast.info('Please fill all required fields');
            return;
        }

        const exists = subRecipeItems.some(
            (item, idx) => item.subRecipeId === subRecipeFormData.subRecipe && idx !== editingSubRecipeIndex
        );
        if (exists && editingSubRecipeIndex === null) {
            toast.info('This sub-recipe already exists');
            return;
        }

        const costPrice = calculateCost(
            subRecipeFormData.quantity, subRecipeFormData.unit, subRecipeFormData.rate,
            subRecipeFormData.uom, subRecipeFormData.new_uom
        );

        const confirmedItem = {
            subRecipeId: subRecipeFormData.subRecipe,
            quantity: subRecipeFormData.quantity,
            uom: subRecipeFormData.uom,
            unit: subRecipeFormData.unit,
            rate: subRecipeFormData.rate,
            costprice: costPrice,
            subRecipeName: subRecipeFormData.subRecipeName,
            new_uom: subRecipeFormData.new_uom || subRecipeFormData.uom,
            link_id: subRecipeFormData.link_id || '',
            sub_recipe_id: subRecipeFormData.sub_recipe_id || subRecipeFormData.subRecipe,
            isDraft: false,
        };

        if (editingSubRecipeIndex !== null) {
            const updated = [...subRecipeItems];
            updated[editingSubRecipeIndex] = confirmedItem;
            setSubRecipeItems(updated);
            setEditingSubRecipeIndex(null);
        } else {
            setSubRecipeItems(prev => {
                const withoutDraft = prev.filter(i => !i.isDraft);
                return [...withoutDraft, confirmedItem];
            });
        }

        setDraftSubRecipeItem(null);
        setSubRecipeFormData({
            subRecipe: '', subRecipeName: '', quantity: '', uom: '', rate: '', costPrice: '', unit: '', new_uom: ''
        });
    };

    const handleEdit = (index) => {
        if (index < 0 || index >= costItems.length) return;

        setShowSubRecipe(false);
        setEditingIndex(index);

        const item = costItems[index];

        const liveDef = unitDefinitions.find(u => u.name === item.costItem);

        let resolvedUom = item.uom;
        let resolvedUnit = item.unit;
        let resolvedRate = item.rate;

        if (liveDef) {
            let normalizedUom = liveDef.uom.toLowerCase();
            if (kgVariations.includes(normalizedUom)) normalizedUom = 'kg';
            else if (gramVariations.includes(normalizedUom)) normalizedUom = 'g';
            else if (literVariations.includes(normalizedUom)) normalizedUom = 'ltr';
            else if (milliliterVariations.includes(normalizedUom)) normalizedUom = 'ml';

            resolvedUom = normalizedUom;
            resolvedUnit = liveDef.unit;
            resolvedRate = liveDef.rate;
        }

        const hasConversion =
            kgVariations.includes(resolvedUom) ||
            gramVariations.includes(resolvedUom) ||
            literVariations.includes(resolvedUom) ||
            milliliterVariations.includes(resolvedUom);

        let resolvedNewUom;
        if (hasConversion) {
            const isKgFamily = kgVariations.includes(resolvedUom) || gramVariations.includes(resolvedUom);
            // const isLtrFamily = literVariations.includes(resolvedUom) || milliliterVariations.includes(resolvedUom);

            const savedNewUom = item.new_uom?.toLowerCase();
            const savedStillValid = isKgFamily
                ? ['gm', 'g', 'kg'].includes(savedNewUom)
                : ['ml', 'ltr', 'l'].includes(savedNewUom);

            resolvedNewUom = savedStillValid ? item.new_uom : (isKgFamily ? 'gm' : 'ml');
        } else {
            resolvedNewUom = resolvedUom;
        }

        setFormData(prev => ({
            ...prev,
            costItem: item.costItem,
            rate: resolvedRate,
            unit: resolvedUnit,
            uom: resolvedUom,
            new_uom: resolvedNewUom,
            quantity: item.quantity.toString(),
            link_id: item.link_id,
            recipe_id: item.recipe_id,
        }));

        setDraftCostItem({
            ...item,
            uom: resolvedUom,
            unit: resolvedUnit,
            rate: resolvedRate,
            new_uom: resolvedNewUom,
            isDraft: true,
            _editingIndex: index,
        });
    };

    const handleSubRecipeEdit = (index) => {
        if (index < 0 || index >= subRecipeItems.length) return;

        setShowSubRecipe(true);
        setEditingSubRecipeIndex(index);
        const item = subRecipeItems[index];

        setSubRecipeFormData(prev => ({
            ...prev,
            subRecipe: item.subRecipeId,
            subRecipeName: item.subRecipeName,
            quantity: item.quantity.toString(),
            uom: item.uom,
            unit: item.unit,
            rate: item.rate,
            new_uom: item.new_uom,
            link_id: item.link_id,
            sub_recipe_id: item.sub_recipe_id || item.subRecipeId,
        }));

        setDraftSubRecipeItem({
            ...item,
            isDraft: true,
            _editingIndex: index,
        });
    };

    const handleCancelEdit = () => {
        setEditingIndex(null);
        setDraftCostItem(null);
        setFormData(prev => ({
            ...prev, costItem: '', rate: '', unit: '', uom: '', quantity: '', new_uom: ''
        }));
    };

    const handleCancelSubRecipeEdit = () => {
        setEditingSubRecipeIndex(null);
        setDraftSubRecipeItem(null);
        setSubRecipeFormData({
            subRecipe: '', subRecipeName: '', quantity: '', uom: '', rate: '', unit: '', new_uom: ''
        });
    };

    const handleDelete = (index) => {
        const updatedItems = costItems.filter((_, i) => i !== index);
        setCostItems(updatedItems);
        if (editingIndex === index) {
            handleCancelEdit();
        }
    };

    const handleSubRecipeDelete = (index) => {
        const updatedItems = subRecipeItems.filter((_, i) => i !== index);
        setSubRecipeItems(updatedItems);
        if (editingSubRecipeIndex === index) {
            handleCancelSubRecipeEdit();
        }
    };

    const showSaveConfirmation = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to save this recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();
                            try {
                                const payload = {
                                    token: token,
                                    outlet: selectedOutlet,
                                    name: formData.menuItem,
                                    cost_price: manualTotalCost,
                                    selling_price: formData.sellingPrice,
                                    cost_percent: profitPercentage,
                                    items: costItems.map(item => ({
                                        name: item.costItem,
                                        rate: item.rate,
                                        uom: item.uom,
                                        new_uom: item.new_uom || item.uom,
                                        quantity: convertToBaseQuantity({
                                            quantity: item.quantity,
                                            fromUom: item.uom,
                                            toUom: item.new_uom,
                                            itemUnit: item.unit,
                                            kgVars: kgVariations,
                                            gVars: gramVariations,
                                            lVars: literVariations,
                                            mlVars: milliliterVariations
                                        }).toString(),
                                        cost: item.costPrice,
                                        unit: item.unit
                                    })),
                                    sub_recipes: subRecipeItems.map(item => ({
                                        sub_recipe_id: item.subRecipeId,
                                        quantity: convertToBaseQuantity({
                                            quantity: item.quantity,
                                            fromUom: item.uom,
                                            toUom: item.new_uom,
                                            itemUnit: item.unit,
                                            kgVars: kgVariations,
                                            gVars: gramVariations,
                                            lVars: literVariations,
                                            mlVars: milliliterVariations
                                        }).toString(),
                                        uom: item.uom,
                                        new_uom: item.new_uom || item.uom,
                                        rate: item.rate,
                                        costprice: item.costprice,
                                        unit: item.unit
                                    })),
                                    item_type: type,
                                    // category: category,
                                    recipe_type: recipeType,
                                    GroupName: groupName
                                };

                                await axios.post(`${baseUrl}/recipe`, payload);
                                toast.success("Recipe saved successfully!");

                                setCostItems([]);
                                setSubRecipeItems([]);
                                setManualTotalCost(0);

                                setFormData({
                                    menuItem: '',
                                    sellingPrice: '',
                                    costItem: '',
                                    rate: '',
                                    unit: '',
                                    uom: '',
                                    quantity: '',
                                    new_uom: '',
                                });

                                setSubRecipeFormData({
                                    subRecipe: '',
                                    subRecipeName: '',
                                    quantity: '',
                                    uom: '',
                                    rate: '',
                                    unit: '',
                                    new_uom: ''
                                });

                                setTimeout(() => navigate(-1), (500));

                            } catch (error) {
                                console.error('Error saving recipe:', error);
                                toast.error(error?.response?.data?.error || error?.response?.data?.message || 'Failed to save recipe');
                            }
                        }}
                    >
                        Save
                    </button>
                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
                toastId: 'save-cost-confirmation'
            }
        );
    };

    const updateConfirmation = () => {
        toast.info(
            <div className="p-2">
                <p className="mb-3 text-white">Are you sure you want to update this recipe?</p>
                <div className="flex justify-between gap-2">
                    <button
                        className="px-4 py-2 bg-green-500 text-white rounded hover:bg-green-600"
                        onClick={async () => {
                            toast.dismiss();

                            try {
                                const payload = {
                                    token: token,
                                    outlet: selectedOutlet,
                                    name: formData.menuItem,
                                    cost_price: manualTotalCost,
                                    selling_price: formData.sellingPrice,
                                    cost_percent: profitPercentage,

                                    items: costItems.map(item => ({
                                        name: item.costItem,
                                        rate: item.rate,
                                        uom: item.uom,
                                        unit: item.unit,
                                        new_uom: item.new_uom || item.uom,
                                        recipe_id: Number(item.recipe_id),
                                        link_id: Number(item.link_id),
                                        quantity: convertToBaseQuantity({
                                            quantity: item.quantity,
                                            fromUom: item.uom,
                                            toUom: item.new_uom,
                                            itemUnit: item.unit,
                                            kgVars: kgVariations,
                                            gVars: gramVariations,
                                            lVars: literVariations,
                                            mlVars: milliliterVariations
                                        }).toString(),
                                        cost: item.costPrice
                                    })),

                                    sub_recipes: subRecipeItems.map(item => ({
                                        sub_recipe_id: item.sub_recipe_id ? Number(item.sub_recipe_id) : undefined,
                                        link_id: item.link_id ? Number(item.link_id) : undefined,
                                        quantity: convertToBaseQuantity({
                                            quantity: item.quantity,
                                            fromUom: item.uom,
                                            toUom: item.new_uom,
                                            itemUnit: item.unit,
                                            kgVars: kgVariations,
                                            gVars: gramVariations,
                                            lVars: literVariations,
                                            mlVars: milliliterVariations
                                        }).toString(),
                                        uom: item.uom,
                                        unit: item.unit,
                                        new_uom: item.new_uom || item.uom,
                                        rate: item.rate,
                                        costprice: item.costprice
                                    })),

                                    item_type: type,
                                    recipe_type: recipeType,
                                    GroupName: groupName
                                };

                                await axios.put(
                                    `${baseUrl}/recipe/${incomingRecipe.id}`,
                                    payload
                                );

                                toast.success("Recipe updated successfully!");

                                setTimeout(() => navigate(-1), (500))

                            } catch (err) {
                                console.error(err);
                                toast.error(err?.response?.data?.error || err?.response?.data?.message || "Error updating recipe");
                            }
                        }}
                    >
                        Update
                    </button>

                    <button
                        className="px-4 py-2 bg-gray-500 text-white rounded hover:bg-gray-600"
                        onClick={() => toast.dismiss()}
                    >
                        Cancel
                    </button>
                </div>
            </div>,
            {
                autoClose: false,
                closeButton: false,
            }
        );
    };

    const handleSave = async () => {
        if (incomingRecipe)
            updateConfirmation();
        else
            showSaveConfirmation();
    };

    const allMenuItemNames = menuItems
        .flatMap(type => type.items.flatMap(category => category.items))
        .map(item => item.Name);

    const allCostItemNames = unitDefinitions.map(item => item.name);

    const showConversionOptions = useMemo(() => {
        return (
            kgVariations.includes(formData.uom?.toLowerCase()) ||
            gramVariations.includes(formData.uom?.toLowerCase()) ||
            literVariations.includes(formData.uom?.toLowerCase()) ||
            milliliterVariations.includes(formData.uom?.toLowerCase())
        );
    }, [formData.uom]);

    const showSubRecipeConversionOptions = useMemo(() => {
        return (
            kgVariations.includes(subRecipeFormData.uom?.toLowerCase()) ||
            gramVariations.includes(subRecipeFormData.uom?.toLowerCase()) ||
            literVariations.includes(subRecipeFormData.uom?.toLowerCase()) ||
            milliliterVariations.includes(subRecipeFormData.uom?.toLowerCase())
        );
    }, [subRecipeFormData.uom]);

    const isNewMenuItem = !menuItems
        .flatMap(type => type.items.flatMap(category => category.items))
        .some(item => item.Name === formData.menuItem);

    useEffect(() => {
        if (!isNewMenuItem) return;

        if (recipeType === "Production") {
            setFormData(prev => ({
                ...prev,
                sellingPrice: manualTotalCost.toFixed(2)
            }));
        }
    }, [manualTotalCost, recipeType, isNewMenuItem]);

    const costItemsTotal = costItems
        .filter((item, idx) => !item.isDraft && idx !== (draftCostItem?._editingIndex ?? -1))
        .reduce((sum, item) => sum + parseFloat(item.costPrice || 0), 0);

    const subRecipesTotal = subRecipeItems
        .filter((item, idx) => !item.isDraft && idx !== (draftSubRecipeItem?._editingIndex ?? -1))
        .reduce((sum, item) => sum + parseFloat(item.costprice || 0), 0);

    const liveCostItemCost = useMemo(() => {
        if (!formData.quantity || !formData.rate || !formData.unit || showSubRecipe)
            return 0;

        try {
            return calculateCost(formData.quantity, formData.unit, formData.rate, formData.uom, formData.new_uom || formData.uom);
        } catch { return 0; }
    }, [formData.quantity, formData.rate, formData.unit, formData.uom, formData.new_uom, showSubRecipe]);

    const liveSubRecipeCost = useMemo(() => {
        if (!subRecipeFormData.quantity || !subRecipeFormData.rate || !subRecipeFormData.unit || !showSubRecipe)
            return 0;

        try {
            return calculateCost(subRecipeFormData.quantity, subRecipeFormData.unit, subRecipeFormData.rate, subRecipeFormData.uom, subRecipeFormData.new_uom || subRecipeFormData.uom);
        } catch { return 0; }
    }, [subRecipeFormData.quantity, subRecipeFormData.rate, subRecipeFormData.unit, subRecipeFormData.uom, subRecipeFormData.new_uom, showSubRecipe]);

    const totalCost = costItemsTotal + subRecipesTotal + liveCostItemCost + liveSubRecipeCost;

    useEffect(() => {
        setManualTotalCost(totalCost);
    }, [totalCost]);

    const profitPercentage = (parseFloat(formData.sellingPrice || 0) > 0
        ? ((parseFloat(manualTotalCost) / parseFloat(formData.sellingPrice)) * 100)
        : 0);

    return (
        <div className='min-h-screen flex flex-col'>
            <Navbar />

            <div className='flex-grow bg-gray-50'>
                <div className="px-10 py-3 text-sm">
                    <div className='flex justify-between mb-3 items-center px-12'>
                        <div className='flex-1 flex justify-start'>
                            <div className="text-lg font-semibold">Recipe Cost Calculation ---</div>
                        </div>

                        <div className="flex-1 flex justify-center">
                            <OutletSearch
                                selectedOutlet={selectedOutlet}
                                setSelectedOutlet={setSelectedOutlet}
                                token={token}
                            />
                        </div>

                        <div className='flex-1 flex justify-end'></div>
                    </div>

                    <MenuCostForm
                        handleAddCostItem={handleAddCostItem}
                        formData={formData}
                        handleMenuItemSelect={handleMenuItemSelect}
                        allCostItemNames={allCostItemNames}
                        allMenuItemNames={allMenuItemNames}
                        handleClearMenuItem={handleClearMenuItem}
                        handleInputChange={handleInputChange}
                        handleSubRecipeInputChange={handleSubRecipeInputChange}
                        handleSubRecipeSelect={handleSubRecipeSelect}
                        handleAddSubRecipe={handleAddSubRecipe}
                        handleCancelEdit={handleCancelEdit}
                        handleCancelSubRecipeEdit={handleCancelSubRecipeEdit}
                        handleCostItemSelect={handleCostItemSelect}
                        showConversionOptions={showConversionOptions}
                        showSubRecipeConversionOptions={showSubRecipeConversionOptions}
                        showSubRecipe={showSubRecipe}
                        setShowSubRecipe={setShowSubRecipe}
                        handleUomChange={handleUomChange}
                        editingIndex={editingIndex}
                        kgVariations={kgVariations}
                        literVariations={literVariations}
                        gramVariations={gramVariations}
                        milliliterVariations={milliliterVariations}
                        subRecipes={subRecipes}
                        subRecipeFormData={subRecipeFormData}
                        editingSubRecipeIndex={editingSubRecipeIndex}
                        menuItems={menuItems}
                        unitDefinitions={unitDefinitions}
                        autoFill={autoFill}
                        setAutoFill={setAutoFill}
                        type={type}
                        setType={setType}
                        recipeType={recipeType}
                        groupName={groupName}
                        setGroupName={setGroupName}
                        handleRecipeTypeChange={handleRecipeTypeChange}
                        isNewMenuItem={isNewMenuItem}
                        costItems={costItems}
                        subRecipeItems={subRecipeItems}
                        handleEdit={handleEdit}
                        handleDelete={handleDelete}
                        handleSave={handleSave}
                        manualTotalCost={manualTotalCost}
                        setManualTotalCost={setManualTotalCost}
                        profitPercentage={profitPercentage}
                        handleSubRecipeDelete={handleSubRecipeDelete}
                        handleSubRecipeEdit={handleSubRecipeEdit}
                        setFormData={setFormData}
                        setSubRecipeFormData={setSubRecipeFormData}
                        draftCostItem={draftCostItem}
                        setDraftCostItem={setDraftCostItem}
                        draftSubRecipeItem={draftSubRecipeItem}
                        setDraftSubRecipeItem={setDraftSubRecipeItem}
                        incomingRecipe={incomingRecipe}
                    />
                </div>
            </div>

            <Footer />
        </div>
    );
};

export default MenuCostPrice;
